import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import PhoneInput from '../shared/PhoneInput';
import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Select from '../shared/Select';
import Autocomplete from '../shared/Autocomplete';

import userRoles, { distributorRoles, countryRoles, companyRoles } from '../../constants/userRoles';

const engineerOptions = [
  { label: 'Level 1 – Senior Engineer', value: 1 },
  { label: 'Level 2 – Mid-Level Engineer', value: 2 },
  { label: 'Level 3 – Entry-Level Engineer', value: 3 }
];

const isAssignableRole = (key, currentUserRole) => (
  key !== userRoles.GLOBAL_ADMIN.name && (
    currentUserRole === userRoles.GLOBAL_ADMIN.name ||
    companyRoles.includes(key) ||
    key === userRoles.SURGEON.name
  )
);

const CreateUserModal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    loading,
    distributorsList = [],
    companies = [],
    currentUser,
    // countriesList = [],
    // manufacturers = [],
    // logisticsTeams = [],
    // radiologyTeams = [],
  } = props;

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState( '');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');

  const [hasPassword, setHasPassword] = useState(false);
  const [password, setPassword] = useState('');

  const [role, setRole] = useState('');
  const [company, setCompany] = useState(null);
  const [administrationCountries, setAdministrationCountries] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [, setManufacturer] = useState(null);
  const [, setLogistics] = useState(null);
  const [, setRadiology] = useState(null);
  const [engineerLevel, setEngineerLevel] = useState(1);

  useEffect(() => {
    if (open && currentUser?.role === userRoles.COUNTRY_ADMIN.name) {
      setCompany({ id: currentUser.company });
    }
  }, [open, currentUser]);

  const rolesOptions = useMemo(() => (
    Object.keys(userRoles)
      .filter((key) => isAssignableRole(key, currentUser.role))
      .map((key) => ({
        label: userRoles[key]?.label,
        value: userRoles[key]?.name,
      }))
  ), [currentUser]);

  const resetValues = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setAddress('');
    setPhone('');
    setPassword('');
    setRole('');
    setCompany(null);
    setDistributors([]);
    setAdministrationCountries([]);
    setManufacturer(null);
    setLogistics(null);
    setRadiology(null);
    setEngineerLevel(null);
  };

  const handleSubmit = async () => {
    const formObj = {
      email: email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      address: address || '',
      phone: phone || '',
      role: role || '',
      password: hasPassword && password ? password : null,
    };

    if (companyRoles.includes(role)) {
      formObj.company = company?.id;
    }

    if (distributorRoles.includes(role) || role === userRoles.SURGEON.name) {
      formObj.distributors = distributors?.map((d) => d.id);
      const countries = [];
      distributors.forEach((d) => {
        if (!countries?.includes(d.country)) {
          countries.push(d.country);
        }
      })
      formObj.administrationCountries = countries;
    }

    if (countryRoles.includes(role)) {
      formObj.administrationCountries = administrationCountries || [];
      formObj.distributors = [];
    }

    // if (role === userRoles.MANUFACTURER.name) {
    //   formObj.manufacturer = manufacturer?.id || '';
    // }
    //
    // if (role === userRoles.LOGISTICS.name) {
    //   formObj.logistics = logistics?.id || '';
    // }
    //
    // if (role === userRoles.RADIOLOGY.name) {
    //   formObj.radiology = radiology?.id || '';
    // }

    if (role === userRoles.ENGINEER.name) {
      formObj.engineerLevel = engineerLevel || 1;
    }

    if ([userRoles.GLOBAL_ADMIN.name, userRoles.COUNTRY_ADMIN.name, userRoles.ENGINEER.name, userRoles.MANUFACTURER.name, userRoles.LOGISTICS.name, userRoles.RADIOLOGY.name].includes(role)) {
      formObj.distributors = [];
    }

    if ([userRoles.GLOBAL_ADMIN.name, userRoles.ENGINEER.name, userRoles.MANUFACTURER.name, userRoles.LOGISTICS.name].includes(role)) {
      formObj.administrationCountries = [];
    }

    await onSubmit(formObj).then(() => {
      resetValues();
    });
  }

  const isDisabled = () => {
    let disabled = !email?.length || !firstName?.length || !lastName?.length || !role?.length;

    if (hasPassword) {
      disabled = disabled || password?.length < 6;
    }

    if (role && companyRoles.includes(role)) {
      disabled = disabled || !company;
    }

    if (role && distributorRoles.includes(role)) {
      disabled = disabled || !distributors?.length;
    }

    if (role && countryRoles.includes(role)) {
      disabled = disabled || !administrationCountries?.length;
    }

    // if (role && role === userRoles.MANUFACTURER.name) {
    //   disabled = disabled || !manufacturer;
    // }
    //
    // if (role && role === userRoles.LOGISTICS.name) {
    //   disabled = disabled || !logistics;
    // }
    //
    // if (role && role === userRoles.RADIOLOGY.name) {
    //   disabled = disabled || !radiology;
    // }

    if (role && role === userRoles.ENGINEER.name) {
      disabled = disabled || !engineerLevel;
    }

    return disabled;
  };

  const distributorsOptions = useMemo(() => {
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name) {
      return distributorsList.filter((d) => currentUser?.administrationCountries?.includes(d.country) && d.company === currentUser?.company);
    }

    if (role === userRoles.SURGEON.name) {
      return distributorsList;
    }

    return distributorsList.filter((d) => d.company === company?.id);
  }, [distributorsList, currentUser, company, role]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title="Create User"
      subtitle="Enter user details"
      submitText="Create"
      disabled={isDisabled() || loading}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' }, maxWidth: { xs: 'auto', md: '560px' } }}>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12 }}>
            <Input
              id="email"
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className="p-t-md p-r-sm">
              <Input
                id="firstName"
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
                required
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className="p-t-md p-l-sm">
              <Input
                id="lastName"
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label="Last Name"
                required
              />
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12 }}>
            <div className="p-t-md">
              <Input
                id="address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder=""
                label="Address"
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <div className="p-t-md">
              <Box sx={{ display: 'flex', marginBottom: '2px' }}>
                <Typography sx={{ fontSize: '14px' }}>Phone Number</Typography>
              </Box>
              <PhoneInput
                value={phone}
                onChange={setPhone}
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <div className="p-t-md">
              <Select
                value={role}
                onChange={setRole}
                options={rolesOptions}
                label='Assigned Role'
                required
              />
            </div>
          </Grid>
          {currentUser?.role === userRoles.GLOBAL_ADMIN.name && companyRoles?.includes(role) && (
            <Grid size={{ xs: 12 }}>
              <div className="p-t-md">
                <Autocomplete
                  value={company}
                  onChange={(value) => {
                    setCompany(value);
                    setAdministrationCountries([]);
                    setDistributors([]);
                  }}
                  options={companies}
                  label="Company"
                  getOptionLabel={(option) => option.name}
                  required
                />
              </div>
            </Grid>
          )}
          {(distributorRoles?.includes(role) || role === userRoles.SURGEON.name) && (
            <Grid size={{ xs: 12 }}>
              <div className="p-t-md">
                <Autocomplete
                  multiple
                  value={distributors}
                  onChange={setDistributors}
                  options={distributorsOptions}
                  label="Distributors"
                  getOptionLabel={(option) => option?.name}
                  required
                />
              </div>
            </Grid>
          )}
          {countryRoles?.includes(role) && (
            <Grid size={{ xs: 12 }}>
              <div className="p-t-md">
                <Autocomplete
                  multiple
                  value={administrationCountries}
                  onChange={setAdministrationCountries}
                  options={currentUser?.role === userRoles.COUNTRY_ADMIN.name ? currentUser?.administrationCountries : (company?.countries || [])}
                  label="Administration Countries"
                  required
                />
              </div>
            </Grid>
          )}
          {role === userRoles.ENGINEER.name && (
            <Grid size={{ xs: 6 }}>
              <div className="p-t-md">
                <Select
                  value={engineerLevel}
                  onChange={setEngineerLevel}
                  options={engineerOptions}
                  label="Engineer Level"
                  required
                />
              </div>
            </Grid>
          )}
          {/*{role === userRoles.MANUFACTURER.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div className="p-t-md">*/}
          {/*      <Autocomplete*/}
          {/*        value={manufacturer}*/}
          {/*        onChange={setManufacturer}*/}
          {/*        options={manufacturers}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Manufacturer"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
          {/*{role === userRoles.LOGISTICS.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div className="p-t-md">*/}
          {/*      <Autocomplete*/}
          {/*        value={logistics}*/}
          {/*        onChange={setLogistics}*/}
          {/*        options={logisticsTeams}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Logistics Team"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
          {/*{role === userRoles.RADIOLOGY.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div className="p-t-md">*/}
          {/*      <Autocomplete*/}
          {/*        value={radiology}*/}
          {/*        onChange={setRadiology}*/}
          {/*        options={radiologyTeams}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Radiology"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
        </Grid>

        <div className='m-t-md'>
          <FormControlLabel
            control={<Checkbox color="primary" checked={hasPassword} onChange={(e) => setHasPassword(e.target.checked)} />}
            label="Default Password"
          />
        </div>

        {hasPassword && (
          <Grid container spacing={0}>
            <Grid size={{ xs: 6 }}>
              <div className="">
                <Input
                  id="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

CreateUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default CreateUserModal;
